import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  STRUCTURE_FILTER_CATEGORIES,
  STRUCTURE_FILTER_FIELD_DEFINITIONS,
} from './structureFilterDefinitions';
import { ACTIVE_STATUS_OPTIONS, EMPTY_FILTERS } from './structureListFilters';
import { StructureListHeader } from './StructureListHeader';
import { useStructureListData } from './useStructureListData';
import { useStructureListReports } from './useStructureListReports';
import { ListType } from '../../../@types';
import { STRUCTURE_NUMERIC_FILTER_FIELDS } from '../../../api/structures';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { TablePagination, AutoRefresh } from '../../../components/molecules/Pagination';
import { ReportTabs, SaveReportDialog } from '../../../components/molecules/ReportTabs';
import {
  StructureFilterBar,
  StructureFilterState,
} from '../../../components/molecules/StructureFilterBar';
import {
  SortModel,
  StructureRowAction,
  StructureTable,
} from '../../../components/organisms/StructureTable';
import { ColumnSelection } from '../../../components/templates/ColumnSelection';
import { PLATFORM_NAME } from '../../../configs/common';
import { SavedReportTypeList, StructureTypeCategory } from '../../../configs/enums';
import { TabHandler } from '../../../handlers/TabHandler';
import { useClientVisibleFieldGuard } from '../../../hooks/useClientVisibleFieldGuard';
import { useIsClient } from '../../../hooks/useIsClient';
import { useListExport } from '../../../hooks/useListExport';
import { usePinnedReports } from '../../../hooks/usePinnedReports';
import { useReportsModal } from '../../../hooks/useReportsModal';
import { useSessionColumnPersist } from '../../../hooks/useSessionColumnPersist';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { mapNumericFilterItemsForWire } from '../../../utils/filterModel';
import { clearSessionColumnState } from '../../../utils/sessionColumnState';
import { getCreateStructurePageUrl } from '../../../utils/structure';
import { toast } from '../../../utils/toast';
import { ReportsModal } from '../../Reports/MyReportsList/ReportsModal';

const ROW_ACTIONS: readonly StructureRowAction[] = [
  { key: 'print_label', label: 'Print Label' },
  { key: 'view_jobs', label: 'View Jobs' },
];

const NO_ROW_ACTIONS: readonly StructureRowAction[] = [];

const StructureListInner: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const permissions = getUserPermissionsQuery.data;
  // useIsClient is fail-closed: returns true while the visibility query is
  // still loading or has errored, so client-only restrictions apply
  // conservatively when the user's visibility state isn't yet known.
  const isClientCustomerUser = useIsClient();
  const { pinnedCodes, setPinnedCodes, defaultTabIndex, reorderTabs } =
    usePinnedReports('StructureList');

  const {
    data,
    isFetching,
    page,
    pageSize,
    total,
    fetchDurationMs,
    sortModel,
    columns,
    columnSelectionData,
    filterModel,
    multiSearchQuery,
    locationTypeOptions,
    contractOptions,
    setPage,
    setPageSize,
    setSortModel,
    applyFilters,
    applyReportConfig,
    updateColumnSelection,
    reorderColumns,
    resetColumnsToDefault,
    restoreDefaultColumns,
    setColumnWidth,
    refetch,
  } = useStructureListData();

  const [filters, setFilters] = useState<StructureFilterState>(EMPTY_FILTERS);
  const [filterBarKey, setFilterBarKey] = useState(0);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(0);
  const [columnSelectionOpen, setColumnSelectionOpen] = useState(false);

  const {
    savedReports,
    activeReport,
    activeReportName,
    defaultTabLabel,
    saveDialogOpen,
    handleReportChange,
    handleSaveNewReport,
    handleOverwriteReport,
    handleSetDefault,
    setSaveDialogOpen,
  } = useStructureListReports({
    filterModel,
    pageSize,
    sortModel,
    columns,
    columnSelectionData,
    applyFilters,
    applyReportConfig,
    resetColumnsToDefault,
    setFilters,
    setFilterBarKey,
  });

  const { recentCodes, isReportsModalOpen, openReportsModal, closeReportsModal } = useReportsModal(
    SavedReportTypeList.StructureList,
    activeReport
  );

  // Persist the default-view column layout to the browser session — debounced so
  // a resize drag writes once on release, not on every frame. Skipped while a
  // saved report is active — that layout belongs to the report, not the session.
  useSessionColumnPersist('StructureList', columns, !activeReport);

  // Default tab context-menu actions.
  const handleRevertToDefaultReport = useCallback(
    () => handleReportChange(null),
    [handleReportChange]
  );
  const handleRevertToSystemDefaults = useCallback(() => {
    const current = savedReports.find((r) => r.isDefault);
    if (current) handleSetDefault({ ...current, isDefault: false });
    clearSessionColumnState('StructureList');
    resetColumnsToDefault();
    setFilters(EMPTY_FILTERS);
    applyFilters(EMPTY_FILTERS);
  }, [savedReports, handleSetDefault, resetColumnsToDefault, setFilters, applyFilters]);

  TabHandler(`List Structure | ${PLATFORM_NAME}`);

  useEffect(() => {
    if (autoRefreshInterval <= 0) return;
    const timer = setInterval(() => refetch(), autoRefreshInterval * 1000);
    return () => clearInterval(timer);
  }, [autoRefreshInterval, refetch]);

  const detailPathFor = useCallback(
    (id: number) =>
      location.pathname.includes('configure') ? `/configure/structure/${id}` : `/structure/${id}`,
    [location.pathname]
  );

  const getRowDetailHref = useCallback(
    (row: Record<string, unknown>) => detailPathFor(Number(row.id)),
    [detailPathFor]
  );

  const { exportAnchor, setExportAnchor, handleExport } = useListExport({
    fileName: ListType.StructureList,
    columns,
    columnSelectionData,
    filterModel,
    // Mirror the list query's numeric-column operator fix so an exported
    // list with a numeric-ID "equals" filter matches what's on screen.
    mapFilterItems: (items) => mapNumericFilterItemsForWire(items, STRUCTURE_NUMERIC_FILTER_FIELDS),
    rowCount: total,
    pageSize,
    sorting: sortModel.sort ? [{ field: sortModel.field, sort: sortModel.sort }] : [],
    // Keep the export in lockstep with the on-screen search.
    multiSearchQuery,
    extra: { structureTypeCategory: StructureTypeCategory.Operational },
  });

  const handleSearch = useCallback(
    (newFilters: StructureFilterState) => {
      setFilters(newFilters);
      applyFilters(newFilters);
    },
    [applyFilters]
  );

  // Lock client/customer users into filtering and sorting by the columns the
  // curated view actually exposes. Non-clients pass through unchanged.
  const visibleFields = useMemo(
    () => columns.filter((c) => c.visible).map((c) => c.key),
    [columns]
  );
  const { restrictedFieldDefinitions, guardSortChange } = useClientVisibleFieldGuard(
    STRUCTURE_FILTER_FIELD_DEFINITIONS,
    visibleFields
  );

  const handleSortChange = useMemo(
    () =>
      guardSortChange<SortModel>((model) => {
        setSortModel({ field: model.field, sort: model.direction });
      }),
    [guardSortChange, setSortModel]
  );

  const handleRowAction = useCallback(
    (id: number, action: string) => {
      if (action === 'view_jobs') {
        navigate(`/services?structureNodeId=${id}`);
        return;
      }
      toast.info(`"${action.replace(/_/g, ' ')}" for node ${id} — coming soon`);
    },
    [navigate]
  );

  const tableData = useMemo(() => data ?? [], [data]);
  const tableSortModel = useMemo<SortModel>(
    () => ({
      field: sortModel.field,
      direction: (sortModel.sort as 'asc' | 'desc') ?? 'asc',
    }),
    [sortModel.field, sortModel.sort]
  );

  return (
    <>
      <StructureListHeader
        total={total}
        permissions={permissions}
        createHref={getCreateStructurePageUrl(location.pathname)}
        exportAnchor={exportAnchor}
        onExportClick={(e) => setExportAnchor(e.currentTarget)}
        onExportClose={() => setExportAnchor(null)}
        onExportCsv={() => handleExport('csv')}
        onExportExcel={() => handleExport('xlsx')}
        onColumnsClick={isClientCustomerUser ? undefined : () => setColumnSelectionOpen(true)}
        onReportsClick={openReportsModal}
      />
      <PageContainer>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <ReportTabs
            activeReport={activeReport}
            recentCodes={recentCodes}
            reports={savedReports}
            onReportChange={handleReportChange}
            onSave={() => setSaveDialogOpen(true)}
            defaultTabLabel={defaultTabLabel}
            onSetDefault={handleSetDefault}
            pinnedCodes={pinnedCodes}
            onPinnedCodesChange={setPinnedCodes}
            defaultTabIndex={defaultTabIndex}
            onReorderTabs={reorderTabs}
            onRevertToSystemDefaults={handleRevertToSystemDefaults}
            onRevertToDefaultReport={handleRevertToDefaultReport}
          />
          <SaveReportDialog
            open={saveDialogOpen}
            onClose={() => setSaveDialogOpen(false)}
            onSaveNew={handleSaveNewReport}
            onOverwrite={handleOverwriteReport}
            activeReportName={activeReportName}
          />

          <StructureFilterBar
            key={filterBarKey}
            onSearch={handleSearch}
            locationTypes={locationTypeOptions}
            contracts={contractOptions}
            activeStatusOptions={ACTIVE_STATUS_OPTIONS}
            fieldDefinitions={restrictedFieldDefinitions ?? STRUCTURE_FILTER_FIELD_DEFINITIONS}
            fieldCategories={STRUCTURE_FILTER_CATEGORIES}
            initialFilters={filters}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: '20px',
              py: '3px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                fontSize: '12px',
                color: 'text.secondary',
              }}
            >
              {total > 0 && (
                <span>
                  Showing {page * pageSize + 1}&ndash;{Math.min((page + 1) * pageSize, total)} of{' '}
                  {total.toLocaleString()}
                </span>
              )}
              {fetchDurationMs !== null && <span>Took {(fetchDurationMs / 1000).toFixed(2)}s</span>}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: '12px',
                color: 'text.secondary',
              }}
            >
              <label htmlFor="rows-per-page-select">Rows:</label>
              <Select
                id="rows-per-page-select"
                size="small"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
                aria-label="Rows per page"
                sx={{
                  fontSize: '12px',
                  height: 26,
                  '& .MuiSelect-select': { py: 0.25, px: 1 },
                }}
              >
                {[25, 50, 100].map((size) => (
                  <MenuItem key={size} value={size} sx={{ fontSize: '12px' }}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
          <StructureTable
            data={tableData}
            columns={columns}
            sortModel={tableSortModel}
            onSortChange={handleSortChange}
            defaultSortField="name"
            getDetailHref={getRowDetailHref}
            rowActions={isClientCustomerUser ? NO_ROW_ACTIONS : ROW_ACTIONS}
            onRowAction={handleRowAction}
            onColumnReorder={reorderColumns}
            onColumnResize={setColumnWidth}
            loading={isFetching}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: '20px',
              pb: 2,
            }}
          >
            <Box sx={{ width: 160 }} />
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
            <AutoRefresh interval={autoRefreshInterval} onChange={setAutoRefreshInterval} />
          </Box>

          <ReportsModal
            open={isReportsModalOpen}
            onClose={closeReportsModal}
            pageName={SavedReportTypeList.StructureList}
            onSelectReport={handleReportChange}
          />
          {!isClientCustomerUser && (
            <ColumnSelection
              open={columnSelectionOpen}
              title="Select Columns"
              value={columnSelectionData}
              onChange={updateColumnSelection}
              onClose={() => setColumnSelectionOpen(false)}
              onReset={restoreDefaultColumns}
            />
          )}
        </Box>
      </PageContainer>
    </>
  );
};

/** Outer wrapper that remounts the inner when the route path changes. */
export const StructureList: React.FC = () => {
  const location = useLocation();
  return <StructureListInner key={location.pathname} />;
};
